import {
  Component,
  OnInit,
  AfterViewInit,
  HostListener,
  ChangeDetectorRef,
  DestroyRef,
  AfterContentChecked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { fromEvent, merge, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ToastNotificationHostComponent } from './toast-notification-host/toast-notification-host.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { ActivityRow } from '../../interfaces/manage/activity.interface';
import { Acp4Api } from '../../services/acp4-api/acp4-api.service';
import { AuthApi } from '../../services/auth-api/auth-api.service';
import { NavigationHelperService } from '../../services/navigation-helper/navigation-helper.service';
import { ToastNotificationsManagerService } from '../../services/toast-notifications-manager/toast-notifications-manager.service';

@Component({
  selector: 'acp-home, [acp-home]',
  templateUrl: './home.component.html',
  imports: [NavbarComponent, RouterOutlet, ToastNotificationHostComponent],
})
export class HomeComponent implements OnInit, AfterViewInit, AfterContentChecked {
  disableTransitionsTimer: ReturnType<typeof setTimeout> | undefined;

  isNavbarVisible = false;

  dateTimeOffsetString: string;

  /*Params for sending user activity*/
  userActivityData: ActivityRow.RawData = {
    CurrentLocation: '',
    PreviousLocation: '',
    NavigateToLocation: '',
    ClientRequestDate: '',
    ClientApplicationVersion: '',
    ClientAPIVersion: '',
    ClientDBVersion: '',
  };

  @HostListener('window:resize')
  /* onResize() will listen to window change and perform certain operations */
  onResize() {
    /* Add class to prevent animations from running when window resizing and remove it after */
    this.disableTransitionsTimer = undefined;
    document.body.classList.add('stop-transitions-on-resize');
    this.disableTransitionsTimer = setTimeout(
      () => document.body.classList.remove('stop-transitions-on-resize'),
      100,
    );
  }

  constructor(
    private toastNotif: ToastNotificationsManagerService,
    private router: Router,
    public authApi: AuthApi,
    private cdRef: ChangeDetectorRef,
    private apiSvc: Acp4Api,
    private navHelp: NavigationHelperService,
    private destroyRef: DestroyRef,
  ) {}

  /**
   * getBrowserInfo()
   * Detects the user's web browser and its version.
   */
  getBrowserInfo() {
    function getBrowserNameAndVersion() {
      const ua = navigator.userAgent;
      let tem;
      const M = /(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i.exec(ua) ?? [];
      if (/trident/i.test(M[1])) {
        tem = /\brv[ :]+(\d+)/g.exec(ua) ?? [];
        return 'IE ' + (tem[1] || '');
      }
      if (M[1] === 'Chrome') {
        tem = /\b(OPR|Edge)\/(\d+)/.exec(ua);
        if (tem != null) return tem.slice(1).join(' ').replace('OPR', 'Opera');
      }

      const browserInfo = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
      if ((tem = /version\/(\d+)/i.exec(ua)) != null) browserInfo.splice(1, 1, tem[1]);
      return browserInfo.join(' ');
    }
    this.userActivityData.ClientUserAgentVersion = getBrowserNameAndVersion();
  }

  /**
   * trackRouterChanges()
   * Allocates the required data to location for user activity logging and other functions
   * based on each time the route changes
   * start and end count limits the potential multiple API calls as many router events are fired on each route change.
   */
  trackRouterChanges() {
    this.navHelp
      .getFilteredRouterEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event instanceof NavigationStart) {
          this.userActivityData.PreviousLocation = this.getSafePath(this.router.url);
          this.userActivityData.CurrentLocation = this.getSafePath(event.url);
        }
        if (event instanceof NavigationEnd) {
          this.getBrowserInfo();
          this.userActivityData.NavigateToLocation = this.getSafePath(this.router.url);
          this.updateNavbarVisible(event.url);

          this.userActivityData.ClientDBVersion = this.apiSvc.dbVersionInfo
            ? JSON.stringify(this.apiSvc.dbVersionInfo)
            : '';
          this.userActivityData.ClientAPIVersion = this.apiSvc.apiVersionInfo
            ? JSON.stringify(this.apiSvc.apiVersionInfo)
            : '';
          this.userActivityData.ClientApplicationVersion = this.apiSvc.appVersionInfo
            ? JSON.stringify(this.apiSvc.appVersionInfo?.projects[0])
            : '';
          this.userActivityData.ClientRequestDate = this.getDatetimeOffsetString(new Date());

          this.submitUserActivityData();
        }
      });
  }

  private getSafePath(url: string): string {
    return url === '/' ? url : url.replace('/', '');
  }

  /**
   * Generates a datetimeoffset string for the given date.
   * The datetimeoffset string includes the date and time in ISO format,
   * along with the time zone offset in +/-HH:MM format.
   *
   * @param date - The date for which to generate the datetimeoffset string.
   * @returns The datetimeoffset string in the format 'YYYY-MM-DDTHH:MM:SS.mmmmmm+/-HH:MM'.
   */
  getDatetimeOffsetString(date: Date) {
    const offset = -date.getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(offset) / 60);
    const offsetMinutes = Math.abs(offset) % 60;
    const offsetSign = offset >= 0 ? '+' : '-';
    const formattedOffset = `${offsetSign}${offsetHours
      .toString()
      .padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;

    const isoString = date.toISOString();
    this.dateTimeOffsetString = `${isoString.substring(0, isoString.length - 1)}${formattedOffset}`;
    return this.dateTimeOffsetString;
  }

  /**
   * submitUserActivityData()
   * makes an API call to submit user activity data on each route change
   */
  submitUserActivityData() {
    if (!this.authApi.isLoggedIn()) return;
    this.apiSvc.post(this.apiSvc.URL_ADD_USER_ACTIVITY, this.userActivityData).subscribe({
      error: err => {
        const content = err?.error?.message;
        if (content?.includes('User must be logged out')) {
          this.authApi.logout();
          // logout clears all notifications
          // we need this setTimeout to ensure the notification is added after logout
          setTimeout(() => {
            this.toastNotif.addNew({ content, type: 'error' });
          }, 50);
          return;
        }
      },
    });
  }

  /**
   * updateNavbarVisible()
   * will look at the string typed in the url and look for "login" by using route.includes
   * if current route is "login" then isNavbarVisible will turn false and in turn remove the navbar from that page.
   * Any other route will keep visibility true and show the navbar.
   *
   * @param route
   */
  updateNavbarVisible(route: string) {
    if (
      route.includes('login') ||
      route.includes('register') ||
      route.includes('forgot-password') ||
      route.includes('reset-password') ||
      route.includes('invite') ||
      route.includes('registration')
    ) {
      this.isNavbarVisible = false;
      return;
    }
    this.isNavbarVisible = true;
  }

  /**
   * onBeforeUnload() gets called through the HostListener right before the page unloads on a refresh,
   * it sets a localStorage is_reloaded item to "reloaded"
   */
  @HostListener('window:beforeunload')
  onBeforeUnload() {
    localStorage.setItem('is_reloaded', 'reloaded');
  }

  /**
   * handlePageReload() is the function thats called once through the navbar constructor
   * IF the localstorage item is_reloaded is set to "reloaded"
   * For now this just handles setting the SystemID on page reload
   */
  handlePageReload() {
    this.authApi.setSystemID();
    localStorage.setItem('is_reloaded', 'false');
  }

  checkNetworkStatus() {
    merge(of(null), fromEvent(window, 'online'), fromEvent(window, 'offline'))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .pipe(map(() => navigator.onLine))
      .subscribe(online => {
        if (!online) {
          this.authApi.logout();
          // logout clears all notifications
          // we need this setTimeout to ensure the notification is added after logout
          setTimeout(() => {
            this.toastNotif.addNew({
              content: 'No internet connection. Please login again',
              type: 'error',
            });
          }, 50);
        }
      });
  }

  ngOnInit(): void {
    this.trackRouterChanges();
    this.checkNetworkStatus();
  }

  ngAfterViewInit(): void {
    if (this.authApi.isLoggedIn()) this.authApi.subscribeToCookieExpiration();
  }

  ngAfterContentChecked() {
    this.cdRef.detectChanges();
  }
}
